'use client';

/**
 * RadixPointerEventsGuard
 *
 * Narrow safety net for the Radix UI Dialog/Popover/Tooltip
 * `body { pointer-events: none }` leak (#257).
 *
 * Radix sets `document.body.style.pointerEvents = 'none'` while a modal
 * surface is open and unsets it on close. Under fast unmount/route-change
 * races (e.g. /reports -> Review -> /incident-report/[callId] -> Skip
 * Waiting), the cleanup misses and body stays locked, freezing the
 * left sidebar and the rest of the document. Toasts still work because
 * they portal to a separate root with explicit pointer-events: auto.
 *
 * When body style changes (and once immediately on mount) this guard checks:
 *   - If `body.style.pointerEvents === 'none'`
 *   - AND no Radix dialog/popover/menu surface is actually open in the DOM
 * ...then it resets `body.style.pointerEvents = ''`.
 *
 * This is intentionally NARROW — it only touches `<body>`, never walks
 * ancestor chains or observes the whole document subtree.
 *
 * Mounted once at the app root via AppShellProvider so it runs on every
 * route that has app chrome.
 */

import { useEffect } from 'react';

// Matches every Radix surface that uses the body pointer-events lock.
// Listing each primitive's data-* attribute explicitly is more robust
// than relying on `role="dialog"` alone (Popover/Tooltip/DropdownMenu
// content elements don't all carry that role).
// role="alertdialog": the shared AlertDialog and the app confirm (components/ui/alert-dialog.tsx, lib/dialogs/confirm.tsx)
// are a Radix Dialog with that role, and Radix never sets a data-radix-alert-dialog-content attribute; without this the
// guard released the modal lock while the confirm was still open (std-overlays G00).
const OPEN_RADIX_CONTENT_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[data-radix-popper-content-wrapper] [data-state="open"]',
  '[data-radix-menu-content][data-state="open"]',
  '[data-radix-popover-content][data-state="open"]',
  '[data-radix-select-content][data-state="open"]',
  '[data-radix-tooltip-content][data-state="open"]',
  '[data-radix-dropdown-menu-content][data-state="open"]',
  '[data-radix-hover-card-content][data-state="open"]',
  '[data-radix-context-menu-content][data-state="open"]',
  '[data-radix-alert-dialog-content][data-state="open"]',
].join(',');

export function RadixPointerEventsGuard() {
  useEffect(() => {
    const check = () => {
      // Cheap inline-style check first — avoids querySelector on every
      // tick when nothing is wrong (the common case).
      if (document.body.style.pointerEvents !== 'none') return;

      // Body is locked. Only release if no Radix surface is actually
      // open — releasing while a real dialog is mid-display would break
      // its modality. The 500ms tick is comfortably longer than Radix's
      // ~150ms close animation, so by the time we run, `data-state` has
      // settled to either "open" (still up) or removed/closed.
      const anyOpen = document.querySelector(OPEN_RADIX_CONTENT_SELECTOR);
      if (anyOpen) return;

      document.body.style.pointerEvents = '';
    };

    // Run once immediately. If the page mounted with body already locked
    // (e.g. HMR mid-dialog, or a SSR + hydration race), we don't want to
    // wait an extra 500ms before recovering.
    check();

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(check);
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });
    return () => observer.disconnect();
  }, []);

  return null;
}

export default RadixPointerEventsGuard;
